import { pubText, pubPath, pubFormat } from "../../i18n/publicTranslation";
import React, { useState } from 'react';
import { Box, ButtonBase, Popover } from '@mui/material';
import Star from '@mui/icons-material/Star';
import snapshot from '../../data/storeRatings.json';

// Store scores are attributed visible content, not first-party review markup.
const labels: Record<string, [string, string]> = {
  en: ['ratings', 'Weighted average'], es: ['valoraciones', 'Media ponderada'],
  ru: ['оценок', 'Средневзвешенная оценка'], fr: ['notes', 'Moyenne pondérée'],
  de: ['Bewertungen', 'Gewichteter Durchschnitt'], it: ['valutazioni', 'Media ponderata'],
  pt: ['avaliações', 'Média ponderada'], nl: ['beoordelingen', 'Gewogen gemiddelde'],
  paddingInlineStart: ['ocen', 'Średnia ważona'], zh: ['条评分', '加权平均评分'],
  ja: ['件の評価', '加重平均'], ko: ['개 평가', '가중 평균'],
  tr: ['değerlendirme', 'Ağırlıklı ortalama'], ar: ['تقييمًا', 'المتوسط المرجح'],
  hi: ['रेटिंग', 'भारित औसत'], uk: ['оцінок', 'Середньозважена оцінка'],
  vi: ['lượt đánh giá', 'Trung bình có trọng số'], bg: ['оценки', 'Претеглена средна оценка'],
  cs: ['hodnocení', 'Vážený průměr'], fi: ['arviota', 'Painotettu keskiarvo'],
  el: ['αξιολογήσεις', 'Σταθμισμένος μέσος όρος'], id: ['penilaian', 'Rata-rata tertimbang'],
  ro: ['evaluări', 'Medie ponderată'], th: ['คะแนน', 'ค่าเฉลี่ยถ่วงน้ำหนัก'],
};

// Weight by the number of ratings, retaining source precision until display.
// These store listings describe US storefronts, not a worldwide rating total.
const ratingCount = snapshot.stores.reduce((total, store) => total + store.ratingCount, 0);
const combinedRating = snapshot.stores.reduce((total, store) => total + store.rating * store.ratingCount, 0) / ratingCount;

const StoreRatings: React.FC<{ language?: string; label: string }> = ({ language = 'en', label }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [ratingsLabel, averageLabel] = labels[language] || labels.en;
  const number = new Intl.NumberFormat(language, { maximumFractionDigits: 1 });
  const summary = `${number.format(combinedRating)} / 5 · ${number.format(ratingCount)} ${ratingsLabel} (US)`;

  return (
    <Box className="landing-hero-redesign__proof-item">
      <ButtonBase
        onClick={event => setAnchorEl(event.currentTarget)}
        aria-label={pubFormat("__VALUE_0__: __VALUE_1__ · App Store + Google Play", [label, summary])}
        aria-haspopup="dialog"
        aria-expanded={Boolean(anchorEl)}
        aria-controls={anchorEl ? 'store-ratings-breakdown' : undefined}
        sx={{
          gap: 'inherit', textAlign: 'inherit', color: 'inherit', borderRadius: 1,
          '&.Mui-focusVisible': { outline: '2px solid currentColor', outlineOffset: 4 },
        }}
      >
        <Box component="span" className="landing-hero-redesign__proof-icon"><Star /></Box>
        <Box component="span">
          <Box component="span" className="landing-hero-redesign__proof-value">
            {pubText(number.format(combinedRating))}
            <Box component="span" className="landing-hero-redesign__proof-stars" aria-hidden="true">★★★★★</Box>
          </Box>
          <Box component="span" className="landing-hero-redesign__proof-label" sx={{ display: 'block' }}>{pubText(label)}</Box>
        </Box>
      </ButtonBase>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box id="store-ratings-breakdown" role="dialog" aria-label={pubText(averageLabel)} sx={{ p: 2, maxWidth: 300 }}>
          <Box sx={{ fontWeight: 700 }}>{pubText(averageLabel)}</Box>
          <Box sx={{ fontSize: '0.8rem', mb: 1.5 }}>{pubText(summary)}</Box>
          {pubText(snapshot.stores.map(store => (
            <Box
              key={store.store}
              component="a"
              href={pubPath(store.url)}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ display: 'block', color: 'inherit', fontSize: '0.8rem', mt: 1 }}
            >
              {pubText(store.store)} ({pubText(store.country)}): {pubText(number.format(store.rating))} / 5
              <br />
              {pubText(number.format(store.ratingCount))} {pubText(ratingsLabel)}
            </Box>
          )))}
        </Box>
      </Popover>
    </Box>
  );
};

export default StoreRatings;
